import React, { useEffect, useState, useCallback } from "react";
import { AdminAuthData } from "../../../data/AdminAuthData";
import { RegistrationData } from "../../../data/RegistrationData";

// Registration Form component
import RegistrationFormAdmin from "./components/RegistrationFormAdmin";

// Error popup
import { ErrorPopup } from "../../../components/ErrorPopup";

// Utility functions
import { validateInput, validateMultiSelectInput, validateEmailInput, validatePhoneNumberInput } from "../../../utility-function/InputValidation";

// API Response
import { AdminRegistrationAPIResponse } from "./types"


export function AdminRegistration(): JSX.Element {

    // Form and state management
    const [adminEmailValue, setAdminEmailValue] = useState<string>('');
    const [validAdminEmail, setValidAdminEmail] = useState<boolean>(true)

    const [adminPasswordValue, setAdminPasswordValue] = useState<string>('');
    const [validAdminPassword, setValidAdminPassword] = useState<boolean>(true)

    const [adminSignedIn, setAdminSignedIn] = useState<boolean>(false);
    const [adminStatusMessage, setAdminStatusMessage] = useState<JSX.Element | undefined>(undefined)

    const [apiResponse, setApiResponse] = useState<AdminRegistrationAPIResponse | null>(null);

    const [selectedLanguage, setSelectedLanguage] = useState<string>('')
    const [validSelectedLanguage, setValidSelectedLanguage] = useState<boolean>(true)

    const [selectedProvider, setSelectedProvider] = useState<string>('');
    const [validSelectedProvider, setValidSelectedProvider] = useState<boolean>(true)

    const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
    const [validSelectedPrograms, setValidSelectedPrograms] = useState<boolean>(true)

    const [firstNameValue, setFirstNameValue] = useState<string>('')
    const [validFirstName, setValidFirstName] = useState<boolean>(true)

    const [lastNameValue, setLastNameValue] = useState<string>('')
    const [validLastName, setValidLastName] = useState<boolean>(true)

    const [phoneValue, setPhoneValue] = useState<string>('')
    const [validPhoneNumber, setValidPhoneNumber] = useState<boolean>(true)

    const [emailValue, setEmailValue] = useState<string>('')
    const [validEmail, setValidEmail] = useState<boolean>(true)

    const [preferredContactMethodValue, setPreferredContactMethod] = useState<string[]>([])
    const [validPreferredContactMethod, setValidPreferredContactMethod] = useState<boolean>(true)

    const [bpCuffValue, setBpCuffValue] = useState<string>('')
    const [validBpCuff, setValidBpCuff] = useState<boolean>(true)

    const [glucometerValue, setGlucometerValue] = useState<string>('')
    const [validGlucometer, setValidGlucometer] = useState<boolean>(true)

    const [subscription, setSubscription] = useState<string>('');
    const [validSubscription, setValidSubscription] = useState<boolean>(true)

    const [statusMessage, setStatusMessage] = useState<JSX.Element | undefined>(undefined);
    const [submissionInProgress, setSubmissionInProgress] = useState<boolean>(false)
    const [errorPopupMessage, setErrorPopupMessage] = useState<string | undefined>(undefined)


    // Function for admin login
    const submitAdminLogin = useCallback((): void => {
        setSubmissionInProgress(true);
        const isAdminEmailValid = validateInput(adminEmailValue);
        const isAdminPasswordValid = validateInput(adminPasswordValue);

        setValidAdminEmail(isAdminEmailValid);
        setValidAdminPassword(isAdminPasswordValid);

        if (!isAdminPasswordValid || !isAdminEmailValid) {
            setSubmissionInProgress(false)
            return;
        }

        AdminAuthData.instance.userAuth(
            adminEmailValue,
            adminPasswordValue,
            (data) => {
                setApiResponse(data);
                setSubmissionInProgress(false);
                setAdminSignedIn(true);
            },
            () => {
                setSubmissionInProgress(false);
                setAdminStatusMessage(
                    <div>
                        <p>Incorrect email or password</p>
                    </div>
                );
            }
        );
    }, [adminEmailValue, adminPasswordValue]);


    // Runs the admin login function when "Enter" is pressed
    useEffect(() => {
        const enterKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return;
            submitAdminLogin();
        };

        document.addEventListener("keydown", enterKeyDown, false);

        return () => {
            document.removeEventListener("keydown", enterKeyDown, false);
        };
    }, [submitAdminLogin]);


    // Handler for when the input information already belongs to a registered user
    const handleAlreadyRegisteredResponse = (
        email_registered: boolean,
        phone_registered: boolean
    ): void => {
        setSubmissionInProgress(false);

        if (email_registered && phone_registered) {
            setStatusMessage(
                <div>
                    <p>
                        The email address <b>{emailValue}</b> and phone number <b>+{phoneValue}</b> have already been
                        registered.
                    </p>
                </div>
            );
        } else if (email_registered) {
            setStatusMessage(
                <div>
                    <p>The email address <b>{emailValue}</b> has already been registered.</p>
                </div>
            );
        } else if (phone_registered) {
            setStatusMessage(
                <div>
                    <p>The phone number <b>+{phoneValue}</b> has already been registered.</p>
                </div>
            );
        }
    };


    // Handler for when the input information is for a new user
    const handleNewRegistration = (): void => {
        RegistrationData.instance.registerUser(
            selectedProvider,
            selectedPrograms,
            selectedLanguage,
            firstNameValue,
            lastNameValue,
            phoneValue,
            emailValue,
            preferredContactMethodValue,
            bpCuffValue,
            glucometerValue,
            "",
            "", // address not used here
            subscription,
            () => {
                // success callback
                setSubmissionInProgress(false)
                setStatusMessage(
                    <div style={{ color: 'green' }}>
                        Success! Patient was sent an email to complete their mobile app account setup.
                    </div>
                )
            },
            (errorMessage?: string) => {
                // error callback
                setSubmissionInProgress(false)
                if (errorMessage) {
                    setErrorPopupMessage(errorMessage)
                } else {
                    setStatusMessage(
                        <div>
                            Your request cannot be processed at the moment. Please try again later.
                        </div>
                    )
                }
            }
        )
    }


    // Function for patient registration
    const registerPatient = useCallback((): void => {

        // Validate inputs
        let isLanguageValid = validateInput(selectedLanguage);
        let isProviderValid = validateInput(selectedProvider);
        let isProgramValid = validateMultiSelectInput(selectedPrograms);
        let isFirstNameValid = validateInput(firstNameValue);
        let isLastNameValid = validateInput(lastNameValue);
        let isPhoneValid = validatePhoneNumberInput(phoneValue);
        let isEmailValid = validateEmailInput(emailValue);
        let isPreferredContantValid = validateMultiSelectInput(preferredContactMethodValue);
        let isSubscriptionValid = validateInput(subscription);

        let isBpCuffValid = true;
        if (selectedPrograms.includes("HTN")) {
            isBpCuffValid = validateInput(bpCuffValue);
        }

        let isGlucometerValid = true;
        if (selectedPrograms.includes("DM") || selectedPrograms.includes("DM_HYBRID")) {
            isGlucometerValid = validateInput(glucometerValue);
        }

        setValidSelectedLanguage(isLanguageValid);
        setValidSelectedProvider(isProviderValid);
        setValidSelectedPrograms(isProgramValid);
        setValidFirstName(isFirstNameValid);
        setValidLastName(isLastNameValid);
        setValidPhoneNumber(isPhoneValid);
        setValidEmail(isEmailValid);
        setValidPreferredContactMethod(isPreferredContantValid);
        setValidSubscription(isSubscriptionValid);
        setValidBpCuff(isBpCuffValid);
        setValidGlucometer(isGlucometerValid);

        if (!isLanguageValid || !isProviderValid || !isProgramValid || !isFirstNameValid || !isLastNameValid ||
            !isPhoneValid || !isEmailValid || !isPreferredContantValid || !isSubscriptionValid || !isBpCuffValid ||
            !isGlucometerValid) {
            return; // Stop if validation fails
        }

        setSubmissionInProgress(true); // Show spinner and disable button

        // Check if user is already registered and take action based on the response
        RegistrationData.instance.userAlreadyRegistered(
            emailValue,
            phoneValue,
            handleAlreadyRegisteredResponse,
            handleNewRegistration
        )
    }, [
        selectedLanguage,
        selectedProvider,
        selectedPrograms,
        firstNameValue,
        lastNameValue,
        phoneValue,
        emailValue,
        preferredContactMethodValue,
        subscription,
        bpCuffValue,
        glucometerValue
    ]
    );

    const form = RegistrationFormAdmin({
        adminEmailValue,
        setAdminEmailValue,
        validAdminEmail,
        adminPasswordValue,
        setAdminPasswordValue,
        validAdminPassword,
        adminSignedIn,
        adminStatusMessage,
        submitAdminLogin,
        apiResponse,
        selectedLanguage,
        setSelectedLanguage,
        validSelectedLanguage,
        selectedProvider,
        setSelectedProvider,
        validSelectedProvider,
        selectedPrograms,
        setSelectedPrograms,
        validSelectedPrograms,
        firstNameValue,
        setFirstNameValue,
        validFirstName,
        lastNameValue,
        setLastNameValue,
        validLastName,
        phoneValue,
        setPhoneValue,
        validPhoneNumber,
        emailValue,
        setEmailValue,
        validEmail,
        preferredContactMethodValue,
        setPreferredContactMethod,
        validPreferredContactMethod,
        bpCuffValue,
        setBpCuffValue,
        validBpCuff,
        glucometerValue,
        setGlucometerValue,
        validGlucometer,
        subscription,
        setSubscription,
        validSubscription,
        statusMessage,
        registerPatient,
        submissionInProgress
    })

    return (
        <>
            {form}
            {errorPopupMessage && (
                <ErrorPopup
                    message={errorPopupMessage}
                    onClose={() => setErrorPopupMessage(undefined)}
                />
            )}
        </>
    )
}